import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RecordingConversation from '../../components/controls/RecordingConversation';
import MobileLayout from '../../components/layout/MobileLayout';
import { Box } from '@mui/material';
import { fetchConversationDetails } from '../../utils/axiosApiRequest';
import { useTranslation } from 'react-i18next';

const MobileRecordingPage = ({
  recording,
  messages,
  wsOpen,
  language,
  selectedPrompt,
  promptTemplates,
  audioReady,
  startRecording,
  stopRecording,
  setLanguage,
  setSelectedPrompt,
  wordCount,
  recordingDuration,
  setShowControls,
  selectedConversation,
  setSelectedConversation
}) => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  // Fetch conversation data if not available after page refresh
  useEffect(() => {
    let isMounted = true;
    
    const fetchConversation = async () => {
      if (!conversationId) {
        navigate('/', { replace: true });
        return;
      }
      
      // Convert IDs to strings for comparison to ensure consistent type
      const currentId = String(conversationId);
      const selectedId = selectedConversation ? String(selectedConversation.id) : null;
      
      // Skip fetching if we already have the correct conversation data
      if (selectedId === currentId) {
        setIsLoading(false);
        return;
      }
      
      try {
        const details = await fetchConversationDetails(currentId);
        
        if (!isMounted) return;
        
        if (details) {
          setSelectedConversation({
            id: currentId,
            ...details
          });
          setIsLoading(false);
        } else {
          navigate('/', { replace: true });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching conversation:', error);
          navigate('/', { replace: true });
        }
      }
    };
    
    fetchConversation();
    
    return () => {
      isMounted = false;
    };
  }, [conversationId, navigate, setSelectedConversation]);

  const handleStopRecording = async () => {
    try {
      const result = await stopRecording();
      setShowControls(false);
      navigate(`/summary/${conversationId}`);
    } catch (error) {
      console.error('Error stopping recording:', error);
      navigate(`/summary/${conversationId}`);
    }
  };

  // Show loading state while fetching conversation data
  if (isLoading) {
    return (
      <MobileLayout title={t('mobileTitle.recording')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          {t('common.loadingConversation')}
        </Box>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title={t('mobileTitle.recording')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <RecordingConversation
          recording={recording}
          startRecording={startRecording}
          stopRecording={handleStopRecording}
          wsOpen={wsOpen}
          language={language}
          setLanguage={setLanguage}
          selectedPrompt={selectedPrompt}
          setSelectedPrompt={setSelectedPrompt}
          promptTemplates={promptTemplates}
          audioReady={audioReady}
          wordCount={wordCount}
          recordingDuration={recordingDuration}
          isMobile={true}
          messages={messages}
        />
      </Box>
    </MobileLayout>
  );
};

export default MobileRecordingPage; 